// Local only. Never passed to fetch, logging, localStorage, or QR serialization.
import { deriveIdentityKey, mnemonicToSeed } from "@link/crypto";
import { entropyToMnemonic, mnemonicToEntropy } from "@link/crypto/bip39";
import { fromBase64url as bytes, toBase64url as b64 } from "@link/shared/api/passkeys";
import { invoke } from "@tauri-apps/api/core";
import { createWebKeyStore } from "@link/storage";

export interface DeviceAccount { slot: string; publicKey: string; accountXid?: string; username: string; displayName?: string; avatarUrl?: string | null; native: boolean }
interface Record extends DeviceAccount { version: 1; key?: CryptoKey; iv?: Uint8Array; ciphertext?: ArrayBuffer }
export const isNativeVault = () => typeof window !== "undefined" && !!(window as unknown as { __LINK_DESKTOP__?: unknown }).__LINK_DESKTOP__;
async function nativeVault<T>(operation: "save" | "read" | "forget", slot: string, value?: string): Promise<T> {
  try { return await invoke<T>("device_vault", { operation, slot, value }); }
  catch {
    // Tauri may reject with a string. Do not leak bridge details or present a
    // misleading passkey error when the OS vault is unavailable.
    throw new Error("Link couldn’t access this device’s secure storage. Unlock your device and use the latest desktop preview, then try again. No browser-storage fallback was used.");
  }
}
function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("link-device-vault-v1", 1);
    request.onupgradeneeded = () => request.result.createObjectStore("accounts", { keyPath: "slot" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error("Link can’t save this device. Allow site storage and try again."));
  });
}
async function readRecords(): Promise<Record[]> {
  const db = await database();
  try { return await new Promise((resolve, reject) => {
    const request = db.transaction("accounts", "readonly").objectStore("accounts").getAll();
    request.onsuccess = () => resolve(request.result as Record[]); request.onerror = () => reject(request.error);
  }); } finally { db.close(); }
}
async function writeRecord(record: Record | string) {
  const db = await database();
  try { await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("accounts", "readwrite"); const store = tx.objectStore("accounts");
    if (typeof record === "string") store.delete(record); else store.put(record);
    tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(new Error("Link couldn’t save your account on this device."));
  }); } finally { db.close(); }
}
export async function listDeviceAccounts(): Promise<DeviceAccount[]> {
  return (await readRecords()).filter(r => r.version === 1 && r.native === isNativeVault()).map(({ slot, publicKey, accountXid, username, displayName, avatarUrl, native }) => ({ slot, publicKey, accountXid, username, displayName, avatarUrl: savedAccountAvatarUrl(accountXid, avatarUrl), native }));
}

// Only Link's public avatar endpoint can be loaded on the signed-out screen.
// Never load an arbitrary remote tracking URL from cached profile metadata.
export function savedAccountAvatarUrl(accountXid?: string, avatarUrl?: string | null): string | null {
  if (!accountXid || !avatarUrl) return null;
  const path = `/v1/avatars/${encodeURIComponent(accountXid)}`;
  return avatarUrl === path || (avatarUrl.startsWith(path + "?v=") && /^\d{1,20}$/.test(avatarUrl.slice(path.length + 3))) ? avatarUrl : null;
}

/** Public presentation metadata only; never changes or creates identity keys. */
export async function updateDeviceAccountProfile(accountXid: string, profile: { username: string; displayName: string; avatarUrl: string | null }): Promise<void> {
  if (!accountXid || !/^[a-z0-9_]{3,32}$/.test(profile.username)) return;
  const db = await database();
  try { await new Promise<void>((resolve, reject) => {
    // Read and update in one transaction. A late profile refresh must not
    // resurrect a forgotten account or overwrite a newly encrypted vault.
    const tx = db.transaction("accounts", "readwrite"); const store = tx.objectStore("accounts");
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result; if (!cursor) return;
      const entry = cursor.value as Record;
      if (entry.version === 1 && entry.accountXid === accountXid && entry.native === isNativeVault()) {
        cursor.update({ ...entry, username: profile.username, displayName: profile.displayName.trim().slice(0, 100) || profile.username, avatarUrl: savedAccountAvatarUrl(accountXid, profile.avatarUrl) });
      }
      cursor.continue();
    };
    tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(new Error("Could not update the saved account picture."));
  }); } finally { db.close(); }
}
const aad = (slot: string) => new TextEncoder().encode(`link.local-vault.v1|${location.origin}|${slot}`);
async function identityForPhrase(phrase: string) {
  const seed = await mnemonicToSeed(phrase);
  try {
    const identity = await deriveIdentityKey(seed);
    const slot = Array.from(identity.publicKeyHash, x => x.toString(16).padStart(2, "0")).join("");
    return { slot, publicKey: b64(identity.publicKeyRaw) };
  } finally { seed.fill(0); }
}
export async function saveDevicePhrase(phrase: string, accountXid?: string, username?: string): Promise<DeviceAccount> {
  const { slot, publicKey } = await identityForPhrase(phrase);
  const previous = (await readRecords()).find(r => r.slot === slot);
  const entropy = await mnemonicToEntropy(phrase);
  const meta: DeviceAccount = { slot, publicKey, accountXid: accountXid ?? previous?.accountXid, username: username ?? previous?.username ?? "Your account", displayName: previous?.displayName, avatarUrl: previous?.avatarUrl, native: isNativeVault() };
  try {
    if (meta.native) {
      // An old/unavailable desktop bridge fails closed, never falls back to browser storage.
      await nativeVault("save", slot, b64(entropy));
      await writeRecord({ ...meta, version: 1 });
    } else {
      const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad(slot) }, key, entropy);
      await writeRecord({ ...meta, version: 1, key, iv, ciphertext });
    }
    if (await readDevicePhrase(slot) !== phrase.trim().toLowerCase().split(/\s+/).join(" ")) throw new Error("Device backup could not be verified");
    return meta;
  } finally { entropy.fill(0); }
}
export async function readDevicePhrase(slot: string): Promise<string> {
  const record = (await readRecords()).find(r => r.slot === slot);
  if (!record || record.version !== 1 || record.native !== isNativeVault()) throw new Error("This device has no saved account. Link it from another device or use account recovery.");
  let entropy: Uint8Array;
  if (record.native) {
    const saved = await nativeVault<string | null>("read", slot);
    if (!saved) throw new Error("The OS could not find this saved account. Use another device or account recovery.");
    entropy = bytes(saved);
  } else {
    if (!record.key || record.key.extractable || !record.iv || !record.ciphertext) throw new Error("Invalid device vault");
    entropy = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: record.iv, additionalData: aad(slot) }, record.key, record.ciphertext));
  }
  try {
    if (entropy.length !== 16) throw new Error("Invalid device vault");
    const phrase = await entropyToMnemonic(entropy);
    const identity = await identityForPhrase(phrase);
    if (identity.slot !== slot || identity.publicKey !== record.publicKey) throw new Error("Device identity mismatch");
    return phrase;
  } finally { entropy.fill(0); }
}
export async function devicePhraseForAccount(accountXid: string) {
  const entry = (await listDeviceAccounts()).find(r => r.accountXid === accountXid);
  return entry ? readDevicePhrase(entry.slot) : null;
}
export async function forgetDeviceAccount(slot: string) {
  const entry = (await listDeviceAccounts()).find(r => r.slot === slot);
  if (!entry) return;
  if (entry.native) await nativeVault("forget", slot);
  if (entry.accountXid) {
    const legacy = createWebKeyStore();
    await legacy.delete(entry.accountXid); await legacy.delete(`${entry.accountXid}:ecdh`);
  }
  await writeRecord(slot);
}
